import { Formik } from 'formik'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ContractorPortalSendCode, ContractorPortalVerifyCode } from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { ContractorPortalVerifyPhoneModalValues } from './ContractorPortalVerifyPhoneModal.interface'
import { ContractorPortalVerifyPhoneModalSchema } from './ContractorPortalVerifyPhoneModal.validator'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { FormErrorMessage } from 'src/components/shared/form/FormErrorMessage'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { Modal, Typography } from '@mui/material'
import { ChevronLeftMinor } from '@shopify/polaris-icons'
import { ResendCode } from 'src/components/shared/ResendCode'
import { getDriver } from 'src/selectors/driver'

interface Props {
  onClose: () => void
  onSuccess: (token: string) => void
}

const initialValues: ContractorPortalVerifyPhoneModalValues = {
  code: '',
}

export const ContractorPortalVerifyPhoneModal = ({ onSuccess, onClose }: Props) => {
  const dispatch = useDispatch()

  const driver = useSelector(getDriver)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const container = useMemo(() => document.getElementById('root'), [])

  const handleFormSubmit = async (values: ContractorPortalVerifyPhoneModalValues) => {
    setIsLoading(true)
    setError(null)

    try {
      const { token }: { token: string } = await createAsyncAction(
        dispatch,
        ContractorPortalVerifyCode.request({ ...values }),
      )

      onSuccess(token)
    } catch (err: any) {
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const sendCode = async () => {
    setIsLoading(true)
    setError(null)

    try {
      await createAsyncAction(dispatch, ContractorPortalSendCode.request())
    } catch (err: any) {
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }
  useEffect(() => {
    sendCode()
  }, [])

  return (
    <Modal
      container={container}
      open
      onClose={onClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
      css={tw`flex justify-center items-center backdrop-blur p-4`}
      disableScrollLock
    >
      <Formik
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        validationSchema={ContractorPortalVerifyPhoneModalSchema}
      >
        {({ handleSubmit }) => (
          <div css={tw`max-w-[590px] mx-4 flex-col p-4 pt-6 bg-shades-0 rounded-2xl`}>
            <Button
              css={tw`bg-primary-100 h-[42px] w-[82px] hover:bg-primary-200`}
              color="light"
              size="medium"
              leftIcon={<ChevronLeftMinor width={19} css={tw`fill-primary-500`} />}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Typography css={tw`mt-6`} variant="h2">
              Verify your phone number
            </Typography>
            <Typography css={tw`text-shades-400 mt-2 mb-6 w-[326px]`}>
              {`We sent a code to the number ending in ${driver?.phone?.slice(
                -4,
              )}. Enter it below to verify and update your
              phone number`}
            </Typography>
            <FormikTextField name="code" css={tw`w-full mb-6`} placeholder="XX-XX-XX" />
            <FormErrorMessage css={tw`mb-6`} error={error} />
            <Button className={tw`w-full mb-6`} loading={isLoading} onClick={() => handleSubmit()}>
              Continue
            </Button>
            <ResendCode onResend={sendCode} />
          </div>
        )}
      </Formik>
    </Modal>
  )
}
